import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Building2, ClipboardCheck, Plane, BarChart3, Waves, Landmark, Anchor, HardHat } from 'lucide-react';
import { services } from '../data/mock';

const iconMap = {
  Building2,
  ClipboardCheck,
  Plane,
  BarChart3,
  Waves,
  Landmark,
  Anchor,
  HardHat
};

// Color rotation based on logo colors
const colorClasses = [
  { bg: 'bg-sky-500', light: 'bg-sky-50', text: 'text-sky-600', border: 'border-sky-200', shadow: 'shadow-sky-100/50' },
  { bg: 'bg-yellow-400', light: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200', shadow: 'shadow-yellow-100/50' },
  { bg: 'bg-green-500', light: 'bg-green-50', text: 'text-green-600', border: 'border-green-200', shadow: 'shadow-green-100/50' },
  { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', shadow: 'shadow-orange-100/50' },
];

const ServiceCard = ({ service, isExpanded, onToggle, colorIndex }) => {
  const IconComponent = iconMap[service.icon];
  const colors = colorClasses[colorIndex % colorClasses.length];

  return (
    <div
      className={`bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${
        isExpanded
          ? `${colors.border} shadow-xl ${colors.shadow}`
          : 'border-slate-200 hover:border-sky-200 hover:shadow-lg'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full p-6 text-left flex items-start gap-4"
      >
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
            isExpanded
              ? `${colors.bg} text-white`
              : `${colors.light} ${colors.text}`
          }`}
        >
          {IconComponent && <IconComponent className="w-6 h-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {service.title}
          </h3>
          <p className="text-slate-500 text-sm line-clamp-2">
            {service.description}
          </p>
        </div>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
            isExpanded
              ? `${colors.light} ${colors.text}`
              : 'bg-slate-100 text-slate-400'
          }`}
        >
          {isExpanded ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>

      {/* Expandable Content */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        } overflow-hidden`}
      >
        <div className="px-6 pb-6 pt-2">
          <div className="border-t border-slate-100 pt-4">
            <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
              Key Services
            </h4>
            <ul className="space-y-2">
              {service.keyServices.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${colors.bg} mt-2 flex-shrink-0`}></div>
                  <span className="text-slate-600 text-sm">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const [expandedId, setExpandedId] = useState(null);

  const handleToggle = (id) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <section id="services" className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block text-sky-500 font-semibold text-sm uppercase tracking-wider mb-3">
            What We Offer
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-800 mb-4">
            Our Services
          </h2>
          <p className="text-slate-600 text-lg">
            Comprehensive consultancy solutions for Maldives resort purchase, headlease
            acquisition, and investing — plus resort development, construction, and
            infrastructure projects.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              isExpanded={expandedId === service.id}
              onToggle={() => handleToggle(service.id)}
              colorIndex={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
